import React, { useRef } from 'react';
import { Box, Textarea, HStack } from '@chakra-ui/react';
import EmojiPickerButton from './EmojiPickerButton';

/**
 * Drop-in replacement for Chakra's <Textarea> with an emoji-picker button
 * docked bottom-right, insert-at-cursor. Same shape as WhatsAppTextarea minus
 * the WhatsApp-markdown formatting toolbar (bold/italic/strike/monospace),
 * for fields — In-App Notification body, Push Notification body — that don't
 * use that syntax.
 */
const EmojiTextarea = ({
    value,
    onChange,
    placeholder,
    rows = 4,
    maxLength,
    isReadOnly,
    bg,
    cursor,
    disabled,
    ...rest
}) => {
    const textareaRef = useRef(null);

    const insertEmoji = (emoji) => {
        const textarea = textareaRef.current;
        if (!textarea || isReadOnly || disabled) return;

        const start = textarea.selectionStart ?? value.length;
        const before = value.slice(0, start);
        const after = value.slice(start);
        const newText = before + emoji + after;

        if (maxLength && newText.length > maxLength) return;

        onChange({ target: { value: newText } });

        requestAnimationFrame(() => {
            textarea.focus();
            const pos = start + emoji.length;
            textarea.setSelectionRange(pos, pos);
        });
    };

    return (
        <Box border="1px solid" borderColor="gray.200" borderRadius="md" overflow="hidden">
            <Textarea
                ref={textareaRef}
                value={value}
                onChange={onChange}
                placeholder={placeholder}
                rows={rows}
                maxLength={maxLength}
                isReadOnly={isReadOnly}
                bg={bg}
                cursor={cursor}
                disabled={disabled}
                border="none"
                borderRadius="0"
                _focus={{ boxShadow: 'none', border: 'none' }}
                resize="vertical"
                {...rest}
            />
            <HStack spacing={1} px={2} py={1} justify="flex-end">
                <EmojiPickerButton onSelect={insertEmoji} isDisabled={isReadOnly || disabled} />
            </HStack>
        </Box>
    );
};

export default EmojiTextarea;
